// auth/tokens.ts — token storage + an authed-fetch helper with one-shot refresh.
//
// Per AUTH_CONTRACT §7:
//   - access + refresh tokens live in localStorage under ember.auth.{token,refreshToken}
//   - cached user under ember.auth.user (id/email/name)
//   - on 401: try refresh once; retry; if still 401, clear tokens and reload.
//
// The fetch helper is exported so api.ts (the /api/turn client) and authApi.ts
// (the auth + save endpoints) share the same retry path. Single source of truth.

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  // The user's locked free-tier scenario; null only mid-signup. Lets the
  // login surface's ScenarioContext snap to a known user's theme the moment
  // they're identified (recovery flow + direct passkey signin). See
  // AUTH_CONTRACT.md (AuthUser shape) and STORIES_CONTRACT.md §1.
  foreverDefaultScenarioId: string | null;
  // Phase A i18n — account-level narration language preference (BCP-47
  // code from i18n/supportedLanguages.ts, or null when never set / English
  // default). Hydrated from /api/auth/me on signed-in mount; updated via
  // PUT /api/auth/language. Null is equivalent to English at the server
  // boundary — routes/turn.ts skips the per-turn language hint when the
  // story's language is "en".
  language: string | null;
  // Mirrors server-side users.is_admin. True iff the logged-in user can
  // see admin-only surfaces in the web app (today: the manual spice
  // override slider in the user menu). Always treated as a UI hint —
  // every admin-only write hits a server endpoint that re-validates the
  // flag against the DB row. UI gating alone is never trusted.
  //
  // Defensive parse: missing / non-boolean from the server (e.g. talking
  // to an older server during a deploy window) coerces to false.
  isAdmin: boolean;
  // Number of passkeys registered to this account. Used to decide whether
  // to show the "skip passkey" option and the strong nag interstitial.
  passkeyCount: number;
  // Number of email-code sign-ins since account creation. Drives the
  // strong-nag threshold (>= 3 logins with 0 passkeys triggers nag).
  emailLoginCount: number;
}

const KEY_TOKEN = "ember.auth.token";
const KEY_REFRESH = "ember.auth.refreshToken";
const KEY_USER = "ember.auth.user";

function safeGet(key: string): string | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function safeRemove(key: string): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function getAccessToken(): string | null {
  return safeGet(KEY_TOKEN);
}

export function getRefreshToken(): string | null {
  return safeGet(KEY_REFRESH);
}

export function getUser(): AuthUser | null {
  const raw = safeGet(KEY_USER);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<AuthUser> | null;
    if (!parsed || typeof parsed.id !== "string") return null;
    // Defensive coercion: an older cached user from before
    // foreverDefaultScenarioId was added will be missing the field. Coerce
    // to null so the login surface falls back to the default theme rather
    // than blowing up on an `undefined` read.
    return {
      id: parsed.id,
      email: typeof parsed.email === "string" ? parsed.email : "",
      name: typeof parsed.name === "string" ? parsed.name : "",
      foreverDefaultScenarioId:
        typeof parsed.foreverDefaultScenarioId === "string"
          ? parsed.foreverDefaultScenarioId
          : null,
      // Phase A i18n — pre-i18n caches won't have this field; coerce to
      // null so the App-level detection effect can decide what to do.
      language:
        typeof parsed.language === "string" ? parsed.language : null,
      // Pre-isAdmin caches won't have the field; coerce to false so
      // admin-only surfaces stay hidden until a fresh /me round-trip
      // updates the cached object.
      isAdmin:
        typeof parsed.isAdmin === "boolean" ? parsed.isAdmin : false,
      passkeyCount:
        typeof parsed.passkeyCount === "number" ? parsed.passkeyCount : 0,
      emailLoginCount:
        typeof parsed.emailLoginCount === "number" ? parsed.emailLoginCount : 0,
    };
  } catch {
    return null;
  }
}

export function setTokens(token: string, refreshToken?: string): void {
  if (token) safeSet(KEY_TOKEN, token);
  if (refreshToken) safeSet(KEY_REFRESH, refreshToken);
}

export function setUser(user: AuthUser): void {
  safeSet(
    KEY_USER,
    JSON.stringify({
      id: user.id,
      email: user.email,
      name: user.name,
      foreverDefaultScenarioId: user.foreverDefaultScenarioId ?? null,
      // Phase A i18n — persisted so it survives reloads without an extra
      // /me round-trip. App.tsx reads + re-fetches in parallel anyway, but
      // having it in the cache keeps the early render path stable.
      language: user.language ?? null,
      // Persist isAdmin so the user menu's admin gate stays stable across
      // reloads. Server is authoritative — every admin-only write
      // independently re-validates the flag on its endpoint.
      isAdmin: user.isAdmin === true,
      passkeyCount: typeof user.passkeyCount === "number" ? user.passkeyCount : 0,
      emailLoginCount: typeof user.emailLoginCount === "number" ? user.emailLoginCount : 0,
    }),
  );
}

export function setAuth(token: string, refreshToken: string, user: AuthUser): void {
  setTokens(token, refreshToken);
  setUser(user);
}

export function clearAuth(): void {
  safeRemove(KEY_TOKEN);
  safeRemove(KEY_REFRESH);
  safeRemove(KEY_USER);
}

export const API_BASE: string = import.meta.env.VITE_API_BASE || "";

// ── Refresh ─────────────────────────────────────────────────────────────

let inflightRefresh: Promise<string | null> | null = null;

/**
 * Calls POST /api/auth/refresh exactly once, even under concurrent 401s.
 * Returns the new access token, or null if refresh failed (caller must clear).
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (inflightRefresh) return inflightRefresh;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  inflightRefresh = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { token?: string };
      if (!data?.token) return null;
      setTokens(data.token);
      return data.token;
    } catch {
      return null;
    } finally {
      inflightRefresh = null;
    }
  })();

  return inflightRefresh;
}

/**
 * Force the user back to the login screen by clearing tokens and reloading.
 * Called when refresh has been tried and failed.
 */
export function forceReauth(): void {
  clearAuth();
  // Avoid reload loops in tests / non-browser environments.
  if (typeof window !== "undefined" && typeof window.location !== "undefined") {
    window.location.reload();
  }
}

// ── Authed fetch with one-shot refresh on 401 ────────────────────────────

export interface AuthedFetchOptions extends RequestInit {
  /** Skip the bearer header even if a token exists. Used by login endpoints. */
  skipAuth?: boolean;
}

/**
 * fetch() with `Authorization: Bearer <token>`. On 401:
 *   - attempt /api/auth/refresh once
 *   - retry the original request with the new token
 *   - on second 401 (or refresh failure) → clearAuth() + reload
 *
 * Returns the Response. The caller is responsible for parsing JSON / handling
 * non-2xx statuses other than 401.
 */
export async function authedFetch(
  path: string,
  options: AuthedFetchOptions = {}
): Promise<Response> {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const { skipAuth, headers, ...rest } = options;

  const buildHeaders = (token: string | null): HeadersInit => {
    const h: Record<string, string> = {
      ...(headers as Record<string, string> | undefined),
    };
    if (rest.body && !h["Content-Type"] && !h["content-type"]) {
      h["Content-Type"] = "application/json";
    }
    if (!skipAuth && token) {
      h["Authorization"] = `Bearer ${token}`;
    }
    return h;
  };

  const token = getAccessToken();
  let res = await fetch(url, { ...rest, headers: buildHeaders(token) });

  if (res.status !== 401 || skipAuth) return res;

  // Try refresh exactly once.
  const newToken = await refreshAccessToken();
  if (!newToken) {
    forceReauth();
    return res;
  }

  res = await fetch(url, { ...rest, headers: buildHeaders(newToken) });
  if (res.status === 401) {
    forceReauth();
  }
  return res;
}
